import { useEffect, useState } from 'react';
import { MdAdd, MdCloudUpload, MdDelete, MdEdit, MdMenuBook, MdRemoveRedEye } from 'react-icons/md';
import {
  addLibraryItem,
  deleteLibraryItem,
  subscribeToLibraryItems,
  updateLibraryItem,
  uploadLibraryFile,
} from '../../services/libraryService';

const CATEGORIES = ['Past Questions', 'Textbooks', 'Novels', 'Study Guides'];
const CLASS_LEVELS = ['jss1', 'jss2', 'jss3', 'ss1', 'ss2', 'ss3', 'utme', 'all'];

const LibraryManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    category: CATEGORIES[0],
    classLevel: 'all',
    isPremium: false,
  });
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToLibraryItems((data) => {
      setItems(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Filter items
  const filteredItems = items.filter(item => {
    const matchSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       item.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const openModal = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setFormData({
        title: item.title,
        author: item.author,
        description: item.description,
        category: item.category,
        classLevel: item.classLevel,
        isPremium: item.isPremium || false,
      });
    } else {
      setCurrentItem(null);
      setFormData({
        title: '',
        author: '',
        description: '',
        category: CATEGORIES[0],
        classLevel: 'all',
        isPremium: false,
      });
    }
    setCoverFile(null);
    setPdfFile(null);
    setIsModalOpen(true);
  };

  const openViewModal = (item) => {
    setViewItem(item);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewItem(null);
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this book?')) {
      await deleteLibraryItem(item.id, item.fileUrl, item.coverUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let coverUrl = currentItem?.coverUrl || '';
      let fileUrl = currentItem?.fileUrl || '';

      if (coverFile) {
        coverUrl = await uploadLibraryFile(coverFile, 'library_covers');
      }
      if (pdfFile) {
        fileUrl = await uploadLibraryFile(pdfFile, 'library_files', (prog) => {
          setUploadProgress(prog);
        });
      }

      const itemPayload = { ...formData, coverUrl, fileUrl };

      if (currentItem) {
        await updateLibraryItem(currentItem.id, itemPayload);
      } else {
        if (!fileUrl) throw new Error('A PDF file is required for new books.');
        await addLibraryItem(itemPayload);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Error saving book.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="w-full pb-6">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-2 mb-4 md:mb-6">
        <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-darkNavy font-poppins">
          Library Management
        </h1>
        <button
          onClick={() => openModal()}
          className="px-3 py-1.5 md:px-4 md:py-2.5 bg-primary text-white rounded-lg md:rounded-xl font-semibold text-xs md:text-sm hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-1 md:gap-2 flex-shrink-0"
        >
          <MdAdd className="text-base md:text-lg" />
          <span className="hidden sm:inline">Add Book</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Total Books</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">{items.length}</p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Free</p>
          <p className="text-base md:text-2xl font-bold text-emerald-600">
            {items.filter(i => !i.isPremium).length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Premium</p>
          <p className="text-base md:text-2xl font-bold text-amber-600">
            {items.filter(i => i.isPremium).length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Categories</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">{CATEGORIES.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm md:text-base"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search books or authors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm md:text-base"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading library items...</div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block lg:hidden space-y-3">
            {filteredItems.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <MdMenuBook className="text-5xl text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No books found</p>
              </div>
            ) : (
              filteredItems.map((item) => (
                <div key={item.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between gap-3 p-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {item.coverUrl ? (
                        <img src={item.coverUrl} alt={item.title} className="w-10 h-12 object-cover rounded shadow-sm flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                          <MdMenuBook className="text-lg text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm truncate">{item.title}</h3>
                        <p className="text-xs text-gray-500 truncate">{item.author}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => openViewModal(item)}
                      className="w-9 h-9 flex-shrink-0 bg-blue-50 text-primary rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all"
                      title="View details"
                    >
                      <MdRemoveRedEye className="text-lg" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 font-semibold text-darkNavy">Title</th>
                  <th className="px-6 py-4 font-semibold text-darkNavy">Category</th>
                  <th className="px-6 py-4 font-semibold text-darkNavy">Class</th>
                  <th className="px-6 py-4 font-semibold text-darkNavy">Access</th>
                  <th className="px-6 py-4 font-semibold text-darkNavy text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {item.coverUrl ? (
                          <img src={item.coverUrl} alt={item.title} className="w-10 h-14 object-cover rounded shadow-sm" />
                        ) : (
                          <div className="w-10 h-14 bg-gray-200 rounded flex items-center justify-center"><MdMenuBook /></div>
                        )}
                        <div>
                          <p className="font-semibold text-darkNavy">{item.title}</p>
                          <p className="text-sm text-gray-500">{item.author}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 uppercase">{item.classLevel}</td>
                    <td className="px-6 py-4">
                      {item.isPremium ? (
                        <span className="px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full">Premium</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full">Free</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => openModal(item)} className="text-blue-500 hover:bg-blue-50 p-2 rounded-lg transition mr-2">
                        <MdEdit />
                      </button>
                      <button onClick={() => handleDelete(item)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition">
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No books found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 md:p-4">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg md:text-xl font-bold text-darkNavy">{currentItem ? 'Edit Book' : 'Add New Book'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <MdAdd className="rotate-45 text-2xl" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    required
                    type="text"
                    className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Author</label>
                  <input
                    required
                    type="text"
                    className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  required
                  rows="3"
                  className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Class Level</label>
                  <select
                    className="w-full px-3 md:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-primary text-sm md:text-base"
                    value={formData.classLevel}
                    onChange={(e) => setFormData({ ...formData, classLevel: e.target.value })}
                  >
                    {CLASS_LEVELS.map(cl => <option key={cl} value={cl}>{cl.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="isPremium"
                  checked={formData.isPremium}
                  onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                  className="w-4 h-4 text-primary rounded border-gray-300"
                />
                <label htmlFor="isPremium" className="text-xs md:text-sm font-medium text-gray-700">Require Premium Subscription</label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-3 md:p-4 text-center hover:border-primary transition">
                  <MdCloudUpload className="mx-auto text-2xl md:text-3xl text-gray-400 mb-2" />
                  <p className="text-xs md:text-sm font-medium text-gray-700">Cover Image</p>
                  <p className="text-[10px] md:text-xs text-gray-500 mb-2">{coverFile?.name || (currentItem?.coverUrl ? 'Current image retained' : 'Upload PNG/JPG')}</p>
                  <input type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files[0])} className="text-xs w-full" />
                </div>
                
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-3 md:p-4 text-center hover:border-primary transition">
                  <MdCloudUpload className="mx-auto text-2xl md:text-3xl text-gray-400 mb-2" />
                  <p className="text-xs md:text-sm font-medium text-gray-700">PDF File <span className="text-red-500">*</span></p>
                  <p className="text-[10px] md:text-xs text-gray-500 mb-2">{pdfFile?.name || (currentItem?.fileUrl ? 'Current file retained' : 'Upload PDF')}</p>
                  <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className="text-xs w-full" />
                </div>
              </div>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4">
                  <div className="bg-primary h-2.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm font-semibold"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 py-2.5 bg-primary text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
                >
                  {isSubmitting ? 'Saving...' : 'Save Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isViewModalOpen && viewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-darkNavy">Book Details</h2>
              <button onClick={closeViewModal} className="text-gray-400 hover:text-gray-600">
                <MdAdd className="rotate-45 text-2xl" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Book Cover */}
              <div className="flex justify-center">
                {viewItem.coverUrl ? (
                  <img src={viewItem.coverUrl} alt={viewItem.title} className="w-32 h-44 object-cover rounded-xl shadow-lg" />
                ) : (
                  <div className="w-32 h-44 bg-gray-200 rounded-xl flex items-center justify-center">
                    <MdMenuBook className="text-5xl text-gray-400" />
                  </div>
                )}
              </div>

              {/* Book Info */}
              <div className="text-center border-b border-gray-100 pb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{viewItem.title}</h3>
                <p className="text-sm text-gray-600">by {viewItem.author}</p>
              </div>

              {/* Details Grid */}
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm font-medium text-gray-500">Category</span>
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold">{viewItem.category}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm font-medium text-gray-500">Class Level</span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold uppercase">{viewItem.classLevel}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm font-medium text-gray-500">Access</span>
                  {viewItem.isPremium ? (
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold">Premium</span>
                  ) : (
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-semibold">Free</span>
                  )}
                </div>

                {viewItem.description && (
                  <div className="pt-2">
                    <p className="text-sm font-medium text-gray-500 mb-2">Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{viewItem.description}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    closeViewModal();
                    openModal(viewItem);
                  }}
                  className="w-full py-2.5 bg-blue-50 text-blue-600 rounded-xl font-semibold hover:bg-blue-100 transition flex items-center justify-center gap-2"
                >
                  <MdEdit /> Edit Book
                </button>
                <button
                  onClick={() => {
                    closeViewModal();
                    handleDelete(viewItem);
                  }}
                  className="w-full py-2.5 bg-red-50 text-red-600 rounded-xl font-semibold hover:bg-red-100 transition flex items-center justify-center gap-2"
                >
                  <MdDelete /> Delete Book
                </button>
                <button
                  onClick={closeViewModal}
                  className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryManagement;
